import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, MessageCircle, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import SectionHeading from "@/components/SectionHeading";
import { FOUNDERS, CONTACT, MODULES } from "@/lib/constants";
import { submitContact, submitDemoRequest } from "@/lib/api";

const COUNTRIES = ["Sri Lanka", "Bangladesh", "India", "Pakistan", "Vietnam", "Cambodia", "Indonesia", "China", "Turkey", "Other"];

export default function Contact() {
  const [params] = useSearchParams();
  const initialTab = params.get("intent") === "demo" || params.get("intent") === "pricing" ? "demo" : "general";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  return (
    <>
      <Toaster richColors position="top-right" />
      <section data-testid="contact-hero" className="bg-[#0A1128] py-24 text-white sm:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
            Contact
          </p>
          <h1 className="mt-4 max-w-4xl font-serif-display text-4xl font-medium leading-[1.05] sm:text-6xl lg:text-7xl">
            Let's make quality, governance and sustainability work for you.
          </h1>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-white/80 sm:text-lg">
            Talk to a founder. Request a tailored demo. Or get a pricing
            proposal for your locations and modules of interest.
          </p>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          {/* LEFT: contact info */}
          <aside className="lg:col-span-5">
            <SectionHeading eyebrow="Direct" title="Reach out to PrimeCore." />
            <div className="mt-8 space-y-3 text-sm">
              <a
                data-testid="contact-info-email"
                href={`mailto:${CONTACT.generalEmail}`}
                className="flex items-center gap-3 rounded-lg border border-[#E2E8F0] bg-white p-4 hover:border-[#E8772E]"
              >
                <Mail size={18} className="text-[#E8772E]" />
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-[#64748B]">General</p>
                  <p className="font-semibold text-[#0A1128]">{CONTACT.generalEmail}</p>
                </div>
              </a>

              {FOUNDERS.filter((f) => f.phone).map((f) => (
                <div
                  key={f.email}
                  data-testid={`contact-card-${f.name.split(" ")[0].toLowerCase()}`}
                  className="rounded-lg border border-[#E2E8F0] bg-white p-5"
                >
                  <p className="font-serif-display text-xl text-[#0A1128]">{f.name}</p>
                  <p className="text-xs uppercase tracking-[0.14em] text-[#E8772E]">{f.role}</p>
                  <div className="mt-4 space-y-2 text-sm text-[#334155]">
                    <a href={`mailto:${f.email}`} className="flex items-center gap-2 hover:text-[#E8772E]">
                      <Mail size={14} className="text-[#475569]" /> {f.email}
                    </a>
                    <a href={`tel:${f.phone.replace(/\s|\(|\)/g, "")}`} className="flex items-center gap-2 hover:text-[#E8772E]">
                      <Phone size={14} className="text-[#475569]" /> {f.phone}
                    </a>
                    {f.whatsapp && (
                      <a
                        href={`https://wa.me/${f.whatsapp.replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-[#0A1128] hover:text-[#E8772E]"
                      >
                        <MessageCircle size={14} className="text-[#25D366]" /> WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              ))}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {CONTACT.offices.map((o) => (
                  <div
                    key={o.country}
                    className="flex items-start gap-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4"
                  >
                    <MapPin size={16} className="mt-1 text-[#E8772E]" />
                    <div>
                      <p className="text-xs uppercase tracking-[0.14em] text-[#64748B]">Registered Office</p>
                      <p className="font-semibold text-[#0A1128]">{o.country}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>

          {/* RIGHT: forms */}
          <div className="lg:col-span-7">
            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList data-testid="contact-tabs" className="grid w-full grid-cols-2 bg-[#F1F5F9]">
                <TabsTrigger data-testid="tab-general" value="general">General Inquiry</TabsTrigger>
                <TabsTrigger data-testid="tab-demo" value="demo">Request a Demo</TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="mt-8">
                <ContactForm />
              </TabsContent>
              <TabsContent value="demo" className="mt-8">
                <DemoForm />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>
    </>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setBusy(true);
    try {
      await submitContact(form);
      toast.success("Thank you — we received your message.");
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="contact-form" onSubmit={onSubmit} className="rounded-lg border border-[#E2E8F0] bg-white p-7 sm:p-9">
      <h3 className="font-serif-display text-2xl text-[#0A1128] sm:text-3xl">Send us a message</h3>
      <p className="mt-2 text-sm text-[#64748B]">We'll respond within 1–2 business days.</p>

      <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field label="Full name *">
          <Input data-testid="contact-name" value={form.name} onChange={onChange("name")} placeholder="Your name" />
        </Field>
        <Field label="Email *">
          <Input data-testid="contact-email" type="email" value={form.email} onChange={onChange("email")} placeholder="you@company.com" />
        </Field>
        <Field label="Company">
          <Input data-testid="contact-company" value={form.company} onChange={onChange("company")} placeholder="Company name" />
        </Field>
        <Field label="Phone">
          <Input data-testid="contact-phone" value={form.phone} onChange={onChange("phone")} placeholder="+94 / +880 ..." />
        </Field>
      </div>

      <div className="mt-5">
        <Field label="Message *">
          <Textarea
            data-testid="contact-message"
            rows={5}
            value={form.message}
            onChange={onChange("message")}
            placeholder="Tell us about your operation and what you're looking for..."
          />
        </Field>
      </div>

      <button
        data-testid="contact-submit"
        disabled={busy}
        type="submit"
        className="mt-7 inline-flex items-center gap-2 rounded-md bg-[#E8772E] px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#D66826] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {busy && <Loader2 size={16} className="animate-spin" />}
        {busy ? "Sending..." : "Send Message"}
      </button>
    </form>
  );
}

function DemoForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    country: "",
    modules: [],
    message: "",
  });
  const [busy, setBusy] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleModule = (id) =>
    setForm((f) => ({
      ...f,
      modules: f.modules.includes(id) ? f.modules.filter((m) => m !== id) : [...f.modules, id],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.country) {
      toast.error("Please fill in name, email, company and country.");
      return;
    }
    setBusy(true);
    try {
      await submitDemoRequest(form);
      toast.success("Demo request received — we'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", role: "", country: "", modules: [], message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="demo-form" onSubmit={onSubmit} className="rounded-lg border border-[#E2E8F0] bg-white p-7 sm:p-9">
      <h3 className="font-serif-display text-2xl text-[#0A1128] sm:text-3xl">Request a tailored Q-Wis demo</h3>
      <p className="mt-2 text-sm text-[#64748B]">Tell us about your operation and which modules interest you.</p>

      <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field label="Full name *">
          <Input data-testid="demo-name" value={form.name} onChange={onChange("name")} placeholder="Your name" />
        </Field>
        <Field label="Email *">
          <Input data-testid="demo-email" type="email" value={form.email} onChange={onChange("email")} placeholder="you@company.com" />
        </Field>
        <Field label="Company *">
          <Input data-testid="demo-company" value={form.company} onChange={onChange("company")} placeholder="Company name" />
        </Field>
        <Field label="Role / Title">
          <Input data-testid="demo-role" value={form.role} onChange={onChange("role")} placeholder="Head of Quality, COO, etc." />
        </Field>
        <Field label="Country *">
          <Select value={form.country} onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}>
            <SelectTrigger data-testid="demo-country"><SelectValue placeholder="Select your country" /></SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((c) => (
                <SelectItem key={c} value={c} data-testid={`demo-country-${c.toLowerCase().replace(/\s/g, '-')}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <div />
      </div>

      <div className="mt-7">
        <Label className="mb-3 block text-xs font-semibold uppercase tracking-[0.16em] text-[#475569]">
          Modules of interest
        </Label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {MODULES.map((m) => {
            const checked = form.modules.includes(m.id);
            return (
              <label
                key={m.id}
                data-testid={`demo-module-${m.id}`}
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${
                  checked ? "border-[#E8772E] bg-[#FFF7F0]" : "border-[#E2E8F0] bg-white hover:border-[#94A3B8]"
                }`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggleModule(m.id)}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-semibold text-[#0A1128]">{m.name}</p>
                  <p className="mt-1 text-xs leading-relaxed text-[#64748B]">{m.description}</p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      <div className="mt-5">
        <Field label="Tell us a bit more (optional)">
          <Textarea
            data-testid="demo-message"
            rows={4}
            value={form.message}
            onChange={onChange("message")}
            placeholder="Number of locations, current quality challenges, timeline..."
          />
        </Field>
      </div>

      <button
        data-testid="demo-submit"
        disabled={busy}
        type="submit"
        className="mt-7 inline-flex items-center gap-2 rounded-md bg-[#E8772E] px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#D66826] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {busy && <Loader2 size={16} className="animate-spin" />}
        {busy ? "Submitting..." : "Request Demo"}
      </button>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-[#475569]">
        {label}
      </Label>
      {children}
    </div>
  );
}
